import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { AccordionHeader, AccordionItem, AccordionPanel, Divider, Accordion as FluentAccordion, MessageBar, MessageBarBody, Portal, SearchBox, Subtitle2Stronger, makeStyles, tokens, } from "@fluentui/react-components";
import { Children, forwardRef, isValidElement, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState, } from "react";
import { ArrowCircleUpRegular, CheckmarkFilled, EditRegular, EyeFilled, EyeOffRegular, FilterRegular, PinFilled, PinRegular } from "@fluentui/react-icons";
import { ToolContext } from "../hoc/fluentToolWrapper";
import { useKeyState } from "../hooks/keyboardHooks";
import { AccordionContext, AccordionItemDepthContext, AccordionSectionBlockContext, useAccordionContext, useAccordionSectionBlockContext, useAccordionSectionItemState, } from "./accordion.contexts";
import { Button } from "./button";
import { CustomTokens } from "./utils";
const useStyles = makeStyles({
    accordion: {
        display: "flex",
        flexDirection: "column",
        height: "100%",
    },
    accordionBody: {
        overflowX: "hidden",
        overflowY: "auto",
        paddingBottom: tokens.spacingVerticalM, // bottom padding since there is no divider at the bottom
    },
    divider: {
        paddingTop: CustomTokens.dividerGap,
        paddingBottom: CustomTokens.dividerGap,
    },
    dividerSmall: {
        paddingTop: CustomTokens.dividerGapSmall,
        paddingBottom: CustomTokens.dividerGapSmall,
    },
    panelDiv: {
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
    },
    highlightDiv: {
        borderRadius: tokens.borderRadiusLarge,
        animationDuration: "1s",
        animationTimingFunction: "ease-in-out",
        animationIterationCount: "5",
        animationFillMode: "forwards",
        animationName: {
            from: {
                boxShadow: `inset 0 0 4px ${tokens.colorTransparentBackground}`,
            },
            // eslint-disable-next-line @typescript-eslint/naming-convention
            "50%": {
                boxShadow: `inset 0 0 12px ${tokens.colorBrandBackground}`,
            },
            to: {
                boxShadow: `inset 0 0 4px ${tokens.colorTransparentBackground}`,
            },
        },
    },
    menuBar: {
        display: "flex",
        padding: `0 ${tokens.spacingHorizontalM}`,
    },
    menuBarControls: {
        display: "flex",
        flexGrow: 1,
        justifyContent: "end",
    },
    sectionItemContainer: {
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
    },
    sectionItemContent: {
        flexGrow: 1,
        minWidth: 0, // Allow content to shrink below its intrinsic width when buttons are shown
        overflow: "hidden",
    },
    sectionItemButtons: {
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        marginRight: tokens.spacingHorizontalXS,
    },
    pinnedContainer: {
        display: "flex",
        flexDirection: "column",
    },
    pinnedContainerEmpty: {
        "&:not(:only-child)": {
            display: "none",
        },
    },
    searchBox: {
        width: "100%",
        maxWidth: "none",
    },
    sectionBlockEmpty: {
        display: "none",
    },
});
/**
 * Renders the menu bar and control buttons.
 *
 * @returns `div`, or `undefined` if all features are disabled.
 */
const AccordionMenuBar = () => {
    AccordionMenuBar.displayName = "AccordionMenuBar";
    const classes = useStyles();
    const accordionCtx = useContext(AccordionContext);
    if (!accordionCtx) {
        return null;
    }
    const { state, dispatch, features } = accordionCtx;
    const { editMode } = state;
    return (_jsxs("div", { className: classes.menuBar, children: [_jsx(AccordionSearchBox, {}), _jsxs("div", { className: classes.menuBarControls, children: [features.hiding && editMode && (_jsxs(_Fragment, { children: [_jsx(Button, { title: "Show all", icon: EyeFilled, appearance: "transparent", onClick: () => dispatch({ type: "SHOW_ALL" }) }), _jsx(Button, { title: "Hide all", icon: EyeOffRegular, appearance: "transparent", onClick: () => {
                                    // Hide all visible (non-hidden) items using the registered item IDs
                                    const { registeredItemIds, state: currentState } = accordionCtx;
                                    const visibleItemIds = Array.from(registeredItemIds.keys()).filter((id) => !currentState.hiddenIds.includes(id));
                                    dispatch({ type: "HIDE_ALL_VISIBLE", visibleItemIds });
                                } })] })), (features.pinning || features.hiding) && (_jsx(Button, { title: "Edit mode", icon: editMode ? CheckmarkFilled : EditRegular, appearance: editMode ? "primary" : "transparent", onClick: () => dispatch({ type: "SET_EDIT_MODE", enabled: !editMode }) }))] })] }));
};
/**
 * Wrapper component that must encapsulate the section headers and panels.
 * - Stores the section ID for use in `AccordionSectionItem`.
 *
 * @param props - `AccordionSectionBlockProps`
 * @returns `AccordionSectionBlockContext.Provider`, or `AccordionItem` if all features are disabled.
 */
const AccordionSectionBlock = (props) => {
    AccordionSectionBlock.displayName = "AccordionSectionBlock";
    const { children, sectionId } = props;
    const classes = useStyles();
    const accordionCtx = useContext(AccordionContext);
    const { context: sectionContext, isEmpty } = useAccordionSectionBlockContext(props);
    if (accordionCtx) {
        return (_jsx(AccordionSectionBlockContext.Provider, { value: sectionContext, children: _jsx(AccordionItem, { value: sectionId, className: isEmpty ? classes.sectionBlockEmpty : undefined, children: children }) }));
    }
    return _jsx(AccordionItem, { value: sectionId, children: children });
};
/**
 * Wrapper component that must encapsulate individual items.
 * - Renders the pin button and tracks the pinned state of the item.
 * - Renders the hide button and tracks the hidden state of the item.
 * - Filters items based on the current search term.
 *
 * @param props - `AccordionSectionItemProps`
 * @returns `Portal` if pinned; `null` if hidden/filtered; `children` otherwise.
 */
export const AccordionSectionItem = (props) => {
    AccordionSectionItem.displayName = "AccordionSectionItem";
    const { children, staticItem } = props;
    const classes = useStyles();
    const accordionCtx = useContext(AccordionContext);
    const itemState = useAccordionSectionItemState(props);
    const [ctrlMode, setCtrlMode] = useState(false);
    const ctrlPressed = useKeyState("Control");
    const [mouseOver, setMouseOver] = useState(false);
    useEffect(() => {
        setCtrlMode(ctrlPressed && mouseOver);
    }, [ctrlPressed, mouseOver]);
    // Override disableCopy so copy buttons appear when ctrl+hovering on this item or when edit mode is active
    const parentToolContext = useContext(ToolContext);
    const editMode = accordionCtx?.state.editMode ?? false;
    const showCopy = ctrlMode || editMode;
    const itemToolContext = useMemo(() => (showCopy ? { ...parentToolContext, disableCopy: false } : parentToolContext), [parentToolContext, showCopy]);
    // If static item or no context, just render children
    if (staticItem || !accordionCtx || !itemState) {
        return _jsx(_Fragment, { children: children });
    }
    const { pinnedContainerRef, features } = accordionCtx;
    const { isNested, isPinned, isHidden, isMatch, pinnedIndex, canMoveUp, inEditMode, actions } = itemState;
    // Nested items just render children (don't show controls)
    if (isNested) {
        return _jsx(_Fragment, { children: children });
    }
    // If hidden (and not in edit mode) or doesn't match search, don't render
    if ((isHidden && !inEditMode) || !isMatch) {
        return null;
    }
    const pinnedContainer = isPinned ? pinnedContainerRef.current : null;
    const showControls = inEditMode || ctrlMode;
    const itemElement = (_jsx(ToolContext.Provider, { value: itemToolContext, children: _jsxs("div", { className: classes.sectionItemContainer, style: isPinned ? { order: pinnedIndex } : undefined, onMouseMove: () => setMouseOver(true), onMouseLeave: () => setMouseOver(false), children: [showControls && (_jsxs("div", { className: classes.sectionItemButtons, children: [features.hiding && (_jsx(Button, { title: isHidden ? "Unhide" : "Hide", icon: isHidden ? EyeOffRegular : EyeFilled, appearance: "transparent", onClick: actions.toggleHidden })), features.pinning && (_jsxs(_Fragment, { children: [_jsx(Button, { title: isPinned ? "Unpin" : "Pin", icon: isPinned ? PinFilled : PinRegular, appearance: "transparent", onClick: actions.togglePinned }), isPinned && _jsx(Button, { icon: ArrowCircleUpRegular, appearance: "transparent", disabled: !canMoveUp, onClick: actions.movePinnedUp })] }))] })), _jsx(AccordionItemDepthContext.Provider, { value: true, children: _jsx("div", { className: classes.sectionItemContent, children: children }) })] }) }));
    return pinnedContainer ? _jsx(Portal, { mountNode: pinnedContainer, children: itemElement }) : itemElement;
};
/**
 * Renders the Pinned section container and defines the portal target for the pinned items.
 *
 * @returns `div`
 */
const AccordionPinnedContainer = () => {
    AccordionPinnedContainer.displayName = "AccordionPinnedContainer";
    const classes = useStyles();
    const accordionCtx = useContext(AccordionContext);
    return (_jsx("div", { ref: accordionCtx?.pinnedContainerRef, className: classes.pinnedContainer, children: _jsx(MessageBar, { className: classes.pinnedContainerEmpty, children: _jsx(MessageBarBody, { children: "No pinned items" }) }) }));
};
/**
 * Renders the search box for filtering items.
 *
 * @returns `SearchBox`, or `null` if the feature is disabled.
 */
const AccordionSearchBox = () => {
    AccordionSearchBox.displayName = "AccordionSearchBox";
    const classes = useStyles();
    const accordionCtx = useContext(AccordionContext);
    if (!accordionCtx?.features.search) {
        return null;
    }
    const { state, dispatch } = accordionCtx;
    return (_jsx(SearchBox, { className: classes.searchBox, appearance: "underline", contentBefore: _jsx(FilterRegular, {}), placeholder: "Filter", value: state.searchTerm, onChange: (_, data) => dispatch({ type: "SET_SEARCH_TERM", term: data.value }) }));
};
/**
 * Wrapper component that must encapsulate the section body.
 *
 * @param props - `AccordionSectionProps`
 * @returns `div`
 */
export const AccordionSection = (props) => {
    AccordionSection.displayName = "AccordionSection";
    const classes = useStyles();
    return _jsx("div", { className: classes.panelDiv, children: props.children });
};
const StringAccordion = FluentAccordion;
export const Accordion = forwardRef((props, ref) => {
    Accordion.displayName = "Accordion";
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { children, highlightSections, uniqueId, enablePinnedItems, enableHiddenItems, enableSearchItems, ...rest } = props;
    const classes = useStyles();
    const { size } = useContext(ToolContext);
    const accordionCtx = useAccordionContext(props);
    const hasPinning = accordionCtx?.features.pinning ?? false;
    const pinnedSectionElement = useMemo(() => {
        return (hasPinning && (_jsx(AccordionSection, { title: "Pinned", collapseByDefault: false, children: _jsx(AccordionPinnedContainer, {}) })));
    }, [hasPinning]);
    // Prevents sections contents from unmounting when closed, allowing their elements to be used in the Pinned section.
    const preventUnmountMotion = useMemo(() => {
        // https://github.com/microsoft/fluentui/issues/34309#issuecomment-2824364945
        // eslint-disable-next-line @typescript-eslint/naming-convention
        return hasPinning ? { children: (Component, props) => _jsx(Component, { ...props, unmountOnExit: false }) } : undefined;
    }, [hasPinning]);
    const validChildren = useMemo(() => {
        return (Children.map([pinnedSectionElement, children], (child) => {
            if (isValidElement(child)) {
                const childProps = child.props;
                if (childProps.title) {
                    return {
                        title: childProps.title,
                        collapseByDefault: childProps.collapseByDefault,
                        content: child,
                    };
                }
            }
            return null;
        })?.filter(Boolean) ?? []);
    }, [children]);
    // Tracks open items, and used to tell the Accordion which sections should be expanded.
    const [openItems, setOpenItems] = useState(validChildren.filter((child) => !child.collapseByDefault).map((child) => child.title));
    // Tracks closed items, which is needed so that when the children change, we only update the open/closed state
    // (depending on the collapseByDefault prop) for items that have not been explicitly opened or closed.
    const [closedItems, setClosedItems] = useState(validChildren.filter((child) => child.collapseByDefault).map((child) => child.title));
    const internalOpenItemsRef = useRef(openItems);
    // When highlight sections is requested, we temporarily override the open items, but if highlight sections is cleared,
    // then we revert back to the normal open items tracking.
    useLayoutEffect(() => {
        if (highlightSections) {
            internalOpenItemsRef.current = [...openItems];
            setOpenItems([...highlightSections]);
        }
        else {
            setOpenItems([...(internalOpenItemsRef.current ?? [])]);
            internalOpenItemsRef.current = undefined;
        }
    }, [highlightSections]);
    useEffect(() => {
        for (const defaultOpenItem of validChildren.filter((child) => !child.collapseByDefault).map((child) => child.title)) {
            // If a child is not marked as collapseByDefault, then it should be opened by default, and
            // it is only "default" if it hasn't already been explicitly added to the opened or closed list.
            if (!closedItems.includes(defaultOpenItem) && !openItems.includes(defaultOpenItem)) {
                setOpenItems((prev) => [...prev, defaultOpenItem]);
            }
        }
    }, [validChildren]);
    const onToggle = useCallback((event, data) => {
        if (data.openItems.includes(data.value)) {
            setOpenItems((prev) => [...prev, data.value]);
            setClosedItems((prev) => prev.filter((item) => item !== data.value));
        }
        else {
            setClosedItems((prev) => [...prev, data.value]);
            setOpenItems((prev) => prev.filter((item) => item !== data.value));
        }
    }, []);
    return (_jsx(StringAccordion, { ref: ref, className: classes.accordion, collapsible: true, multiple: true, onToggle: onToggle, openItems: openItems, ...rest, children: _jsxs(AccordionContext.Provider, { value: accordionCtx, children: [_jsx(AccordionMenuBar, {}), _jsx("div", { className: classes.accordionBody, children: validChildren.map((child, index) => {
                        const isHighlighted = highlightSections?.includes(child.title);
                        return (_jsxs(AccordionSectionBlock, { sectionId: child.title, children: [_jsxs("div", { className: isHighlighted ? classes.highlightDiv : undefined, children: [_jsx(AccordionHeader, { size: size, children: _jsx(Subtitle2Stronger, { children: child.title }) }), _jsx(AccordionPanel, { collapseMotion: preventUnmountMotion, children: _jsx("div", { className: classes.panelDiv, children: child.content }) })] }), index < validChildren.length - 1 && _jsx(Divider, { inset: true, className: size === "small" ? classes.dividerSmall : classes.divider })] }, child.content.key ?? child.title));
                    }) })] }) }));
});
//# sourceMappingURL=accordion.js.map